import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiUser, FiArrowRight, FiCheck, FiCalendar, FiPieChart, FiShield, FiUsers, FiLayers, FiActivity } from 'react-icons/fi';
import { HiOutlineUserGroup, HiOutlineOfficeBuilding, HiOutlineClock, HiOutlineSparkles, HiOutlineLightningBolt, HiOutlineGlobe } from 'react-icons/hi';

const RegisterOptions = () => {
  const [hoveredOption, setHoveredOption] = useState(null);

  const accountTypes = [
    {
      id: 'individual',
      title: 'Individual Account',
      tagline: 'For personal productivity',
      description: 'Perfect for freelancers, students, and anyone who wants to stay organized with powerful task management tools.',
      icon: FiUser,
      accentColor: 'emerald',
      features: [
        { icon: FiCheck, text: 'Unlimited tasks and projects' },
        { icon: FiCalendar, text: 'Google calendar sync' },
        { icon: FiPieChart, text: 'Personal productivity analytics' },
        { icon: HiOutlineClock, text: 'Smart reminders & deadlines' },
        { icon: HiOutlineSparkles, text: 'AI-powered task suggestions' },
      ],
      idealFor: ['Freelancers', 'Students', 'Personal projects'],
      cta: 'Create Individual Account',
      path: '/auth/register'
    },
    {
      id: 'organization',
      title: 'Organization Account',
      tagline: 'For teams and businesses',
      description: 'Built for teams who need collaboration, roles, permissions, and shared workspaces with enterprise-grade security.',
      icon: HiOutlineOfficeBuilding,
      accentColor: 'blue',
      popular: true,
      features: [
        { icon: HiOutlineUserGroup, text: 'Team workspaces and collaboration' },
        { icon: FiShield, text: 'Role-based access control (RBAC)' },
        { icon: HiOutlineGlobe, text: 'Domain-based auto-join' },
        { icon: FiLayers, text: 'Departments and team structure' },
        { icon: FiActivity, text: 'Organization analytics & audit logs' },
      ],
      idealFor: ['Startups', 'Growing teams', 'Enterprises'],
      cta: 'Create Organization',
      path: '/auth/register-organization'
    }
  ];

  const getAccentClasses = (color, type) => {
    const colors = {
      emerald: {
        bg: 'bg-emerald-50 dark:bg-emerald-900/30',
        bgHover: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/50',
        border: 'border-emerald-200 dark:border-emerald-700',
        borderHover: 'hover:border-emerald-300 dark:hover:border-emerald-600',
        text: 'text-emerald-600 dark:text-emerald-400',
        icon: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400',
        button: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200 dark:shadow-emerald-900/30',
        ring: 'ring-emerald-200 dark:ring-emerald-700',
        check: 'text-emerald-500 dark:text-emerald-400'
      },
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/30',
        bgHover: 'hover:bg-blue-100 dark:hover:bg-blue-900/50',
        border: 'border-blue-200 dark:border-blue-700',
        borderHover: 'hover:border-blue-300 dark:hover:border-blue-600',
        text: 'text-blue-600 dark:text-blue-400',
        icon: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
        button: 'bg-blue-600 hover:bg-blue-700 shadow-blue-200 dark:shadow-blue-900/30',
        ring: 'ring-blue-200 dark:ring-blue-700',
        check: 'text-blue-500 dark:text-blue-400'
      }
    };
    return colors[color]?.[type] || '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-blue-900/30">
                <img src="/icon.png" alt="Tasklina" className="w-7 h-7 object-contain" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">Tasklina</span>
            </Link>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">Already have an account?</span>
              <Link 
                to="/auth/login" 
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                Sign in
                <FiArrowRight className="ml-1.5 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-semibold mb-6 border border-blue-100 dark:border-blue-800">
            <HiOutlineLightningBolt className="w-4 h-4 mr-2" />
            Get started in under 3 minutes
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Choose Your Account Type
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
            Whether you're working solo or with a team, Tasklina adapts to your workflow.
            Select the option that best fits your needs.
          </p>
        </div>

        {/* Account Type Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {accountTypes.map((type) => {
            const Icon = type.icon;
            const isHovered = hoveredOption === type.id;
            const accentColor = type.accentColor;
            
            return (
              <div
                key={type.id}
                className={`relative bg-white dark:bg-gray-800 rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                  type.popular 
                    ? `border-blue-300 dark:border-blue-600 shadow-xl shadow-blue-100 dark:shadow-blue-900/20` 
                    : `border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 shadow-lg hover:shadow-xl dark:shadow-gray-900/20`
                }`}
                onMouseEnter={() => setHoveredOption(type.id)}
                onMouseLeave={() => setHoveredOption(null)}
              >
                {/* Popular Badge */}
                {type.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-blue-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                      RECOMMENDED
                    </div>
                  </div>
                )}

                <div className="p-8">
                  {/* Card Header */}
                  <div className="flex items-start space-x-4 mb-6">
                    <div className={`w-14 h-14 rounded-xl ${getAccentClasses(accentColor, 'icon')} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{type.title}</h2>
                      <p className={`text-sm font-medium ${getAccentClasses(accentColor, 'text')}`}>
                        {type.tagline}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    {type.description}
                  </p>

                  {/* Ideal For Section */}
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                      Ideal for
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {type.idealFor.map((item, idx) => (
                        <span 
                          key={idx} 
                          className={`px-3 py-1.5 rounded-full text-sm font-medium ${getAccentClasses(accentColor, 'bg')} ${getAccentClasses(accentColor, 'text')} border ${getAccentClasses(accentColor, 'border')}`}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="mb-8">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                      What's included
                    </p>
                    <ul className="space-y-3">
                      {type.features.map((feature, idx) => {
                        const FeatureIcon = feature.icon;
                        return (
                          <li key={idx} className="flex items-center space-x-3">
                            <div className={`w-5 h-5 rounded-full ${getAccentClasses(accentColor, 'bg')} flex items-center justify-center flex-shrink-0`}>
                              <FeatureIcon className={`w-3 h-3 ${getAccentClasses(accentColor, 'text')}`} />
                            </div>
                            <span className="text-gray-700 dark:text-gray-300 text-sm">{feature.text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={type.path}
                    className={`w-full inline-flex items-center justify-center px-6 py-4 text-base font-semibold rounded-xl text-white transition-all duration-200 ${getAccentClasses(accentColor, 'button')} shadow-lg`}
                  >
                    {type.cta}
                    <FiArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Free to Start</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">No credit card required. Start with our free tier and upgrade when you need more.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-4">
                <FiShield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Secure by Default</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Enterprise-grade security with encryption, RBAC, and audit logging built in.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 flex items-center justify-center mx-auto mb-4">
                <HiOutlineSparkles className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Flexible Upgrade</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Switch between plans anytime. Individual users can create organizations later.</p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
            Not sure which to choose?
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            Start with <Link to="/auth/register" className="font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300">Individual</Link> and you can always create an organization later.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterOptions;
